import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { StudentTheme, StudentThemeService } from '../../services/student-theme.service';
import { AppTheme } from './app-theme';
import { resolveBrandingLogoUrl } from './branding-logo-source';
import {
  applyHomeLetterLogoCssVar,
  applyStudentThemeTokens,
  writeCachedStudentTheme,
  updateFavicon,
} from './student-theme-tokens-applier';

/**
 * Loads active student theme from /api/student-theme and applies CSS variables + logos.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly siteLogoSubject = new BehaviorSubject<string>(AppTheme.siteLogoSrc);
  /** Home hero — API home_letter or assets/saferoads-logo(1).png */
  private readonly homeLogoSubject = new BehaviorSubject<string>(AppTheme.homeLogoSrc);
  /** Letters — API home_letter or assets/SAFEROADSLOGOWSLOGAN.png */
  private readonly letterLogoSubject = new BehaviorSubject<string>(AppTheme.homeLetterLogoSrc);

  constructor(private studentThemeService: StudentThemeService) {}

  readonly siteLogoUrl$ = this.siteLogoSubject.asObservable();
  readonly homeLogoUrl$ = this.homeLogoSubject.asObservable();
  /** @deprecated Prefer homeLogoUrl$ for home; letter logo is applied via CSS var */
  readonly homeLetterLogoUrl$ = this.letterLogoSubject.asObservable();

  getSiteLogoUrl(): string {
    return this.siteLogoSubject.value;
  }

  getHomeLogoUrl(): string {
    return this.homeLogoSubject.value;
  }

  getHomeLetterLogoUrl(): string {
    return this.letterLogoSubject.value;
  }

  async loadAndApply(): Promise<void> {
    try {
      const theme = await firstValueFrom(this.studentThemeService.getActiveTheme());
      this.applyTheme(theme);
    } catch (error) {
      console.warn('Student theme unavailable; using default SCSS theme.', error);
    }
  }

  applyTheme(theme: StudentTheme | null | undefined): void {
    if (!theme) {
      return;
    }

    if (theme.tokens) {
      applyStudentThemeTokens(theme.tokens);
    }

    const siteLogo = resolveBrandingLogoUrl(
      theme.siteLogoUrl ?? theme.logoUrl,
      AppTheme.siteLogoSrc
    );
    // Same API slot (home_letter); different static fallbacks per surface.
    const homeLogo = resolveBrandingLogoUrl(
      theme.homeLetterLogoUrl,
      AppTheme.homeLogoSrc
    );
    const letterLogo = resolveBrandingLogoUrl(
      theme.homeLetterLogoUrl,
      AppTheme.homeLetterLogoSrc
    );

    applyHomeLetterLogoCssVar(letterLogo);
    updateFavicon(letterLogo);
    this.siteLogoSubject.next(siteLogo);
    this.homeLogoSubject.next(homeLogo);
    this.letterLogoSubject.next(letterLogo);

    writeCachedStudentTheme({
      tokens: theme.tokens ?? {},
      siteLogoUrl: theme.siteLogoUrl ?? theme.logoUrl ?? null,
      homeLetterLogoUrl: theme.homeLetterLogoUrl ?? null,
    });
  }

  /** Re-apply cached logos (e.g. after reading cache in main.ts). */
  applyCachedLogos(siteLogoUrl?: string | null, homeLetterLogoUrl?: string | null): void {
    const siteLogo = resolveBrandingLogoUrl(siteLogoUrl, AppTheme.siteLogoSrc);
    const homeLogo = resolveBrandingLogoUrl(homeLetterLogoUrl, AppTheme.homeLogoSrc);
    const letterLogo = resolveBrandingLogoUrl(
      homeLetterLogoUrl,
      AppTheme.homeLetterLogoSrc
    );
    applyHomeLetterLogoCssVar(letterLogo);
    updateFavicon(letterLogo);
    this.siteLogoSubject.next(siteLogo);
    this.homeLogoSubject.next(homeLogo);
    this.letterLogoSubject.next(letterLogo);
  }
}
